import React, {useContext, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {get, map} from 'lodash';
import {I18nReactContext} from '../../../contexts/I18nContext';
import {getArchivedUserSignDocVersionsAPI} from '../../../Services/api';
import {SignDocCard} from '../../SignDoc/SignDocCard/SignDocCard';
import {PageTitle} from '../../PageTitle/PageTitle';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import './UserSignDocVersionsPage.scss';

/**
 * UserSignDocVersionsPage - Displays all archived versions for a SignDoc group
 * Accessible from the Learning Center archived tab
 *
 * Features:
 * - Lists all user's archived versions of a recurring SignDoc
 * - Shows progress and completion status per version
 * - Allows navigation back to archived list
 */
export const UserSignDocVersionsPage = () => {
    const {groupRootId} = useParams();
    const {i18n} = useContext(I18nReactContext);

    const [versions, setVersions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Fetches all archived versions for the group
     */
    const fetchVersions = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const versionsData = await getArchivedUserSignDocVersionsAPI({groupRootId});

            // Ensure we have an array before sorting
            if (!Array.isArray(versionsData)) {
                setVersions([]);
                return;
            }

            // Sort by groupVersion descending (newest first)
            const sortedVersions = [...versionsData].sort((a, b) => {
                return get(b, 'signDoc.groupVersion', 0) - get(a, 'signDoc.groupVersion', 0);
            });
            setVersions(sortedVersions);
        } catch (err) {
            console.error('Error fetching archived versions:', err);
            setError(i18n.translate('errors.generalServerError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (groupRootId) {
            fetchVersions();
        }
    }, [groupRootId]);


    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Get SignDoc name from first version for title
    const signDocName = get(versions, '[0].signDoc.docName', '');

    if (error || versions.length === 0) {
        return (
            <div className="UserSignDocVersionsPage" data-testid="user-versions-page">
                <div className="emptyState" data-testid="empty-state">
                    <h2>{i18n.translate('learningCenter.versionsNotFound')}</h2>
                    <p>{error || i18n.translate('learningCenter.versionsNotFoundDescription')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="UserSignDocVersionsPage MenuPage" data-testid="user-versions-page">
            <PageTitle
                title={i18n.translate('learningCenter.archivedVersions')}
                subTitle={signDocName}
                size="small"
            />
            <div className="menuInnerPage">
                <div className="versionsList" data-testid="versions-list">
                    {map(versions, (userSignDoc) => {
                        const signDoc = get(userSignDoc, 'signDoc', {});

                        return (
                            <div
                                className="versionCardWrapper"
                                key={userSignDoc.id}
                                data-testid={`version-card-${userSignDoc.id}`}
                            >
                                <SignDocCard
                                    {...userSignDoc}
                                    {...signDoc}
                                    additionalData={get(userSignDoc, 'additionalData', {})}
                                    signDocAdditionalData={get(signDoc, 'additionalData', {})}
                                    status="archived"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default UserSignDocVersionsPage;
